import * as ko from "knockout";
import { createPopper, Instance as PopperInstance, Placement } from "@popperjs/core";
import { ZIndexManager } from 'Core/Common/ZIndexManager';

// -------------Examples of usage----------------
// 1. Direct string
//      <a data-bind="popper: { content: '<div>Hello world</div>', showOnHover: true }">Hover me</a>
// 2. Observable string
//      const myContent = ko.observable("<div>Dynamic content</div>");
//      <a data-bind="popper: { content: myContent, showOnHover: true }">Hover me</a>
// 3. Knockout template
//      ko.templates['myTemplate'] = `<div><!-- ko foreach: items --><span data-bind="text: $data"></span><!--/ko--></div>`;
//      <a data-bind="popper: { content: { name: 'myTemplate', data: { items: myItems } }, showOnHover: true }">Hover me</a>
// 4. Data Attr
//      <a data-bind="popper: { content: { name: 'myTemplate', data: { items: myItems } }"
//         data-z-index="9999"
//         data-placement="right-start"
//         data-offset="[0,12]"
//         data-class="my-click"
//         data-strategy="fixed"
//         data-gpu="true"
//         data-hover="true"
//         data-hover-persistent="true">
//             Hover Me
//      </a>
// ----------------------------------------------

type KOString = string | KnockoutObservable<string> | { name: string; data?: any };

export interface IPopperBindingConfig {
    content?: KOString;
    placement?: Placement;
    offset?: [number, number];
    className?: string;
    strategy?: "absolute" | "fixed";
    gpuAcceleration?: boolean;
    showOnHover?: boolean;
    showOnClick?: boolean;
    zIndex?: number;
    hoverPersistent?: boolean;
}

interface PopperDomData {
    instance: PopperInstance;
    popperEl: HTMLElement;
    subscription?: KnockoutSubscription;
    observer?: MutationObserver;
    setContent: (value: KOString) => void;
    cleanup: () => void;
}

const DOM_DATA_KEY = "__ko_popper";

export const PopperTooltipExtention = {
    Init() {
        ko.bindingHandlers.popper = {
            init: (element, valueAccessor) => {
                const cfgFromBinding = ko.unwrap(valueAccessor()) as IPopperBindingConfig;
                const defaultZIndex = ZIndexManager.Instance.NextValue;

                const cfg: IPopperBindingConfig = {
                    placement: element.getAttribute("data-placement") as Placement || cfgFromBinding.placement,
                    offset: (() => {
                        if (element.hasAttribute("data-offset")) {
                            try {
                                return JSON.parse(element.getAttribute("data-offset") ?? "[0,8]");
                            } catch {
                                console.warn("Invalid data-offset on element", element);
                                return [0, 8];
                            }
                        }
                        return cfgFromBinding.offset;
                    })(),
                    className: element.getAttribute("data-class") || cfgFromBinding.className,
                    strategy: (element.getAttribute("data-strategy") as "absolute" | "fixed") || cfgFromBinding.strategy,
                    gpuAcceleration: element.hasAttribute("data-gpu")
                        ? element.getAttribute("data-gpu") === "true"
                        : cfgFromBinding.gpuAcceleration,
                    content: cfgFromBinding.content || element.getAttribute("data-content"),
                    showOnHover: element.hasAttribute("data-hover")
                        ? element.getAttribute("data-hover") === "true"
                        : cfgFromBinding.showOnHover,
                    showOnClick: element.hasAttribute("data-click")
                        ? element.getAttribute("data-click") === "true"
                        : cfgFromBinding.showOnClick,
                    hoverPersistent: element.hasAttribute("data-hover-persistent")
                        ? element.getAttribute("data-hover-persistent") === "true"
                        : cfgFromBinding.hoverPersistent,
                    zIndex: (() => {
                        if (element.hasAttribute("data-z-index")) {
                            const z = parseInt(element.getAttribute("data-z-index") ?? `${defaultZIndex}`, 10);
                            return isNaN(z) ? defaultZIndex : z;
                        }
                        return cfgFromBinding.zIndex ?? defaultZIndex;
                    })(),
                };

                const popperEl = document.createElement("div");
                popperEl.className = `ko-popper${cfg.className ? " " + cfg.className : ""}`;
                Object.assign(popperEl.style, {
                    position: cfg.strategy === "fixed" ? "fixed" : "absolute",
                    zIndex: `${cfg.zIndex}`,
                    display: cfg.showOnHover || cfg.showOnClick ? "none" : "block",
                });
                document.body.appendChild(popperEl);

                const setContent = (value: KOString) => {
                    ko.cleanNode(popperEl);
                    const context = ko.contextFor(element);

                    if (typeof value === 'string') {
                        popperEl.innerHTML = value;
                        if (context) {
                            setTimeout(() => {
                                ko.applyBindingsToDescendants(context, popperEl);
                                if (popperEl.style.display !== "none") instance.update();
                            }, 0);
                        } else if (popperEl.style.display !== "none") {
                            instance.update();
                        }
                    } else if (ko.isObservable(value)) {
                        popperEl.innerHTML = ko.unwrap(value);
                        if (context) {
                            setTimeout(() => {
                                ko.applyBindingsToDescendants(context, popperEl);
                                if (popperEl.style.display !== "none") instance.update();
                            }, 0);
                        } else if (popperEl.style.display !== "none") {
                            instance.update();
                        }
                    } else if ('name' in value && value.name) {
                        ko.renderTemplate(value.name, value.data ?? context?.$data, {}, popperEl, 'replaceChildren');
                        if (popperEl.style.display !== "none") {
                            instance.update();
                        }
                    } else {
                        popperEl.innerHTML = "";
                    }
                };
                setContent(cfg.content || "");

                const instance = createPopper(element, popperEl, {
                    placement: cfg.placement ?? "top",
                    strategy: cfg.strategy ?? "absolute",
                    modifiers: [
                        { name: "offset", options: { offset: cfg.offset ?? [0, 8] } },
                        { name: "flip", options: { fallbackPlacements: ["top", "bottom", "right", "left"] } },
                        { name: "preventOverflow", options: { padding: 8 } },
                        { name: "computeStyles", options: { gpuAcceleration: cfg.gpuAcceleration ?? true } },
                        { name: "eventListeners", enabled: false },
                    ],
                });

                const update = () => { if (popperEl.style.display !== "none") instance.update(); };
                window.addEventListener("scroll", update, true);
                window.addEventListener("resize", update, true);

                let subscription: KnockoutSubscription | undefined;
                if (ko.isObservable(cfg.content)) {
                    subscription = (cfg.content as KnockoutObservable<string>).subscribe(val => setContent(val));
                }

                let isUpdating = false;
                const observer = new MutationObserver(() => {
                    if (!isUpdating && popperEl.style.display !== "none") {
                        isUpdating = true;
                        instance.update();
                        isUpdating = false;
                    }
                });
                observer.observe(element, { attributes: true, childList: true, subtree: true });

                let mouseEnterHandler: ((e: MouseEvent) => void) | undefined;
                let mouseLeaveHandler: ((e: MouseEvent) => void) | undefined;

                const show = () => {
                    popperEl.style.display = "block";
                    instance.setOptions({ modifiers: [{ name: "eventListeners", enabled: true }] });
                    instance.update();
                };
                const hide = () => {
                    popperEl.style.display = "none";
                    instance.setOptions({ modifiers: [{ name: "eventListeners", enabled: false }] });
                };

                if (cfg.showOnHover) {
                    if (cfg.hoverPersistent) {
                        mouseEnterHandler = () => show();
                        mouseLeaveHandler = (e: MouseEvent) => {
                            const related = e.relatedTarget as Node | null;
                            if (!related || (!popperEl.contains(related) && related !== element)) {
                                hide();
                            }
                        };
                        element.addEventListener("mouseenter", mouseEnterHandler);
                        element.addEventListener("mouseleave", mouseLeaveHandler);
                        popperEl.addEventListener("mouseenter", mouseEnterHandler);
                        popperEl.addEventListener("mouseleave", mouseLeaveHandler);
                    } else {
                        mouseEnterHandler = () => show();
                        mouseLeaveHandler = () => hide();
                        element.addEventListener("mouseenter", mouseEnterHandler);
                        element.addEventListener("mouseleave", mouseLeaveHandler);
                    }
                }

                const toggle = (e?: Event) => {
                    e?.stopPropagation();
                    if (popperEl.style.display === "none") show();
                    else hide();
                };
                const hideOnClickOutside = (e: Event) => {
                    if (!popperEl.contains(e.target as Node) && e.target !== element) hide();
                };

                if (cfg.showOnClick) {
                    element.addEventListener("click", toggle);
                    document.addEventListener("click", hideOnClickOutside);
                }

                const cleanup = () => {
                    window.removeEventListener("scroll", update, true);
                    window.removeEventListener("resize", update, true);
                    subscription?.dispose?.();
                    observer.disconnect();

                    if (cfg.showOnHover) {
                        if (mouseEnterHandler) {
                            element.removeEventListener("mouseenter", mouseEnterHandler);
                            popperEl.removeEventListener("mouseenter", mouseEnterHandler);
                        }
                        if (mouseLeaveHandler) {
                            element.removeEventListener("mouseleave", mouseLeaveHandler);
                            popperEl.removeEventListener("mouseleave", mouseLeaveHandler);
                        }
                    }
                    if (cfg.showOnClick) {
                        element.removeEventListener("click", toggle);
                        document.removeEventListener("click", hideOnClickOutside);
                    }

                    try { ko.cleanNode(popperEl); } catch {}

                    instance.destroy();
                    popperEl.remove();
                };

                const data: PopperDomData = { instance, popperEl, subscription, observer, setContent, cleanup };
                ko.utils.domData.set(element, DOM_DATA_KEY, data);
                ko.utils.domNodeDisposal.addDisposeCallback(element, cleanup);
            },

            update: (element, valueAccessor) => {
                const cfg = ko.unwrap(valueAccessor()) as IPopperBindingConfig;
                const data = ko.utils.domData.get(element, DOM_DATA_KEY) as PopperDomData | undefined;
                if (!data) return;

                data.instance.setOptions(opts => ({
                    ...opts,
                    placement: cfg.placement ?? opts.placement,
                    strategy: cfg.strategy ?? opts.strategy,
                    modifiers: [
                        { name: "offset", options: { offset: cfg.offset ?? [0, 8] } },
                        { name: "flip", options: { fallbackPlacements: ["top", "bottom", "right", "left"] } },
                        { name: "preventOverflow", options: { padding: 8 } },
                        { name: "computeStyles", options: { gpuAcceleration: cfg.gpuAcceleration ?? true } },
                        { name: "eventListeners", enabled: data.popperEl.style.display !== "none" },
                    ],
                }));

                if (!ko.isObservable(cfg.content)) {
                    setTimeout(() => data.setContent(cfg.content ?? ""), 0);
                } else if (data.popperEl.style.display !== "none") {
                    data.instance.update();
                }
            },
        };
    },
};
